import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  resetPassword,
} from "../../services/api";
import SwiftAlert from "../../components/SwiftAlert";
import Logo2 from "../../assets/Logo2.png";

const ForgotPassword = () => {
  const navigate = useNavigate();

  // --- STATE ---
  const [step, setStep] = useState(1); // 1 = Email, 2 = OTP, 3 = New Password
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    password: "",
    confirmPassword: "",
  });

  // ✅ ALL REQUIRED: 12+ chars, uppercase, number, special (spaces don't count as special)
  const SPECIAL_CHAR_REGEX = /[^A-Za-z0-9\s]/;

  const [validations, setValidations] = useState({
    length: false,
    uppercase: false,
    number: false,
    special: false,
  });

  // Refs for the 6 PIN inputs
  const inputRefs = useRef([]);

  // --- VALIDATION LOGIC ---
  const checkPasswordStrength = (pass) => {
    setValidations({
      length: pass.length >= 12,
      uppercase: /[A-Z]/.test(pass),
      number: /[0-9]/.test(pass),
      special: SPECIAL_CHAR_REGEX.test(pass),
    });
  };

  // --- INPUT HANDLER ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError("");

    if (name === "password") {
      checkPasswordStrength(value);
    }
  };

  // --- PIN INPUT HANDLERS (For Step 2) ---
  const handlePinChange = (e, index) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;

    const newDigit = val.slice(-1);
    const currentCode = formData.otp.split("");
    while (currentCode.length < 6) currentCode.push("");
    currentCode[index] = newDigit;

    setFormData((prev) => ({ ...prev, otp: currentCode.join("").slice(0, 6) }));

    if (newDigit && index < 5) inputRefs.current[index + 1].focus();
  };

  const handlePinKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!formData.otp[index] && index > 0) {
        inputRefs.current[index - 1].focus();
        const currentCode = formData.otp.split("");
        currentCode[index - 1] = "";
        setFormData((prev) => ({ ...prev, otp: currentCode.join("") }));
      }
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6).split("");
    if (pasteData.every((char) => !isNaN(char))) {
      const newCode = [...pasteData, ...Array(6).fill("")].slice(0, 6).join("");
      setFormData((prev) => ({ ...prev, otp: newCode }));
      const focusIndex = Math.min(pasteData.length, 5);
      inputRefs.current[focusIndex].focus();
    }
  };

  const extractErrorMessage = (err) => {
    const data = err?.response?.data;
    if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0]?.msg || "Invalid input.";
    }
    return data?.message || err?.message || "An error occurred.";
  };

  // --- SUBMIT LOGIC ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError("");
    setSuccessMsg("");

    try {
      // STEP 1: SEND OTP
      if (step === 1) {
        if (!formData.email) throw new Error("Please enter your email address.");

        const response = await sendForgotPasswordOtp({ email: formData.email });
        if (response?.success) {
          setStep(2);
          setSuccessMsg("Verification code sent to your email.");
        } else {
          throw new Error(response?.message || "Failed to send OTP.");
        }
      }

      // STEP 2: VERIFY OTP
      else if (step === 2) {
        if (formData.otp.length !== 6) throw new Error("Please enter the full 6-digit code.");

        const response = await verifyForgotPasswordOtp({
          email: formData.email,
          code: formData.otp,
        });

        if (response?.success) {
          setStep(3);
          setSuccessMsg("Code verified. Please set a new password.");
          checkPasswordStrength(formData.password);
        } else {
          throw new Error(response?.message || "Invalid OTP.");
        }
      }

      // STEP 3: RESET PASSWORD
      else if (step === 3) {
        if (!formData.password || !formData.confirmPassword) {
          throw new Error("Please fill in all fields.");
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error("Passwords do not match.");
        }

        // ✅ Enforce ALL required rules
        if (!validations.length || !validations.uppercase || !validations.number || !validations.special) {
          throw new Error(
            "Password must be 12+ characters and include at least 1 uppercase letter, 1 number, and 1 special character."
          );
        }

        const response = await resetPassword({
          email: formData.email,
          otp: formData.otp,
          newPassword: formData.password,
        });

        if (response?.success) {
          navigate("/login", {
            state: {
              email: formData.email,
              successMessage: "Password reset successfully! Please login.",
            },
          });
        } else {
          throw new Error(response?.message || "Failed to reset password.");
        }
      }
    } catch (err) {
      setFormError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Helper Component for Validation Items
  const ValidationItem = ({ met, label }) => (
    <div
      className={`flex items-center gap-2 text-xs font-medium transition-colors ${
        met ? "text-emerald-600" : "text-slate-400"
      }`}
    >
      <i className={`fa-solid ${met ? "fa-circle-check" : "fa-circle"} text-[10px]`}></i>
      <span>{label}</span>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-screen flex items-center justify-center p-4 font-sans relative overflow-hidden">
      <style>{`input::-ms-reveal, input::-ms-clear { display: none; }`}</style>

      {formError && <SwiftAlert type="error" message={formError} onClose={() => setFormError("")} />}
      {successMsg && <SwiftAlert type="success" message={successMsg} onClose={() => setSuccessMsg("")} />}

      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-blue-400/20 rounded-full blur-3xl pointer-events-none md:hidden"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-purple-400/20 rounded-full blur-3xl pointer-events-none md:hidden"></div>

      <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-slate-100 md:min-h-[600px] z-10">
        {/* MOBILE HEADER */}
        <div className="md:hidden bg-blue-600 p-8 pb-12 text-white relative overflow-hidden">
          <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-white/10 rounded-full blur-xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-6 opacity-90">
              <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center border border-white/10 overflow-hidden">
                <img src={Logo2} alt="ReviewMate logo" className="w-full h-full object-contain rounded-xl scale-150" />
              </div>
              <span className="font-bold tracking-wide text-sm uppercase">ReviewMate</span>
            </div>
            <h2 className="text-2xl font-bold leading-tight">Account Recovery</h2>
            <p className="text-blue-100 text-sm mt-1">Let's get you back on track.</p>
          </div>
        </div>

        {/* DESKTOP SIDEBAR */}
        <div className="hidden md:flex md:w-1/2 bg-blue-600 p-12 flex-col justify-between text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-900 opacity-20 rounded-full -ml-10 -mb-10 blur-2xl"></div>

          <div className="relative z-10">
            <Link to="/login" className="flex items-center gap-2 mb-8 hover:opacity-80 transition text-sm font-medium">
              <i className="fa-solid fa-arrow-left"></i> Back to Login
            </Link>
            <h2 className="text-3xl font-bold leading-tight mb-4">Forgot Password?</h2>
            <p className="text-blue-100 text-sm leading-relaxed opacity-90">
              Don't worry, it happens. Verify your identity and secure your account with a new password.
            </p>
          </div>

          <div className="space-y-4 relative z-10">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-blue-400"></div>
              <p className="font-medium text-sm italic opacity-90">"Mistakes are the portals of discovery."</p>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Form */}
        <div className="w-full md:w-1/2 px-6 py-8 md:p-12 flex flex-col justify-center relative">
          <div className="animate-[fadeIn_0.3s_ease-out]">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* STEP 1: EMAIL */}
              {step === 1 && (
                <div className="animate-[fadeIn_0.3s_ease-out]">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-slate-900">Find your account</h3>
                    <p className="text-slate-500 text-sm mt-1">Enter your email to receive a verification code.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      placeholder="student@university.edu"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400"
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              {/* STEP 2: OTP */}
              {step === 2 && (
                <div className="flex flex-col items-center justify-center text-center animate-[fadeIn_0.5s_ease-out]">
                  <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-6">
                    <i className="fa-solid fa-envelope-open-text text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Check your email</h3>
                  <p className="text-sm text-slate-500 mb-8">
                    We sent a 6-digit code to <br />
                    <span className="font-bold text-slate-800">{formData.email}</span>
                  </p>

                  <div className="flex gap-2 justify-center mb-4" onPaste={handlePinPaste}>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                        maxLength="1"
                        value={formData.otp[index] || ""}
                        onChange={(e) => handlePinChange(e, index)}
                        onKeyDown={(e) => handlePinKeyDown(e, index)}
                        className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl font-bold rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none transition text-slate-800 bg-slate-50 focus:bg-white"
                      />
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-xs text-slate-400 hover:text-slate-600 underline transition"
                  >
                    Wrong email? Go Back
                  </button>
                </div>
              )}

              {/* STEP 3: NEW PASSWORD */}
              {step === 3 && (
                <div className="animate-[fadeIn_0.3s_ease-out]">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-slate-900">Reset Password</h3>
                    <p className="text-slate-500 text-sm mt-1">Create a strong password for your account.</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          placeholder="••••••••"
                          className="w-full px-4 py-3 pr-10 rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400"
                          onChange={handleInputChange}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition p-1 cursor-pointer z-10"
                        >
                          <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                        </button>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-4 bg-slate-50 p-3 rounded-lg border border-slate-100">
                        <ValidationItem met={validations.length} label="12+ Characters (required)" />
                        <ValidationItem met={validations.special} label="1 Special Character (required)" />
                        <ValidationItem met={validations.uppercase} label="Uppercase Letter (required)" />
                        <ValidationItem met={validations.number} label="Number (required)" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                        Confirm Password
                      </label>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        placeholder="••••••••"
                        className={`w-full px-4 py-3 rounded-lg border outline-none transition text-sm text-slate-700 placeholder-slate-400 ${
                          formData.confirmPassword && formData.password !== formData.confirmPassword
                            ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                            : "border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                        }`}
                        onChange={handleInputChange}
                      />
                      {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                        <p className="text-red-500 text-xs mt-1 font-medium">Passwords do not match</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* SUBMIT BUTTON */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-600/20 mt-2 active:scale-[0.98] flex items-center justify-center gap-2 ${
                  isLoading ? "opacity-80 cursor-not-allowed" : ""
                }`}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    {step === 1 && "Send Code"}
                    {step === 2 && "Verify Code"}
                    {step === 3 && "Reset Password"}
                  </>
                )}
              </button>

              {step === 1 && (
                <div className="text-center mt-4">
                  <Link
                    to="/login"
                    className="text-sm font-semibold text-slate-500 hover:text-blue-600 transition"
                  >
                    Back to Login
                  </Link>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
